import { AriaRole, CSSProperties, FC } from 'react';

import { purpurBreakpointLg, purpurBreakpointMd, purpurBreakpointSm } from '@purpur/library/tokens';
import { type ImageProps as NextImageProps } from 'next/image';

import { contentfulImageResponsiveUrl } from '@telialt/utils/src/Contentful/contentfulImageResponsiveUrl';
import { buildContentfulImageSrcSet } from '@telialt/utils/src/Contentful/contentfulImageResponsiveUrl/buildContentfulImageSrcSet';
import { contentfulImageUrl } from '@telialt/utils/src/Contentful/contentfulImageUrl';

type SizesMap = { sm?: `${number}vw`; md?: `${number}vw`; lg?: `${number}vw`; other?: `${number}vw` };

type ContentfulImageProps = {
    src: string;
    sizes?: string | SizesMap;
    fill?: boolean;
    alt: string;
    role?: AriaRole;
    width?: number;
    height?: number;
    style?: CSSProperties;
    loading?: 'lazy' | 'eager';
    [key: `data-${string}`]: string | undefined;
};

const isNextEnv = process.env.IS_NEXT === 'true';
const NextImage: FC<NextImageProps> = isNextEnv ? require('next/image').default : () => null;

const buildSizes = (sizes?: SizesMap) =>
    `(min-width: ${purpurBreakpointLg}) ${sizes?.lg || '100vw'}, (min-width: ${purpurBreakpointMd}) ${sizes?.md || '100vw'}, (min-width: ${purpurBreakpointSm}) ${sizes?.sm || '100vw'}, ${sizes?.other || '100vw'}`;

/**
 * A wrapper around next/image that uses the Contentful custom image loader.
 * The loader appends `fm=webp` and `w={width}` query parameters so that Contentful
 * serves images at the appropriate size and in WebP format.
 * The original Contentful CDN domain is preserved — images are not proxied through /_next/image.
 * On applications that don't use Next.js, the component becomes based on vanilla HTML <img> with srcSet.
 */
export const ContentfulImage: FC<ContentfulImageProps> = ({
    src,
    sizes,
    fill,
    alt,
    role,
    width,
    height,
    style,
    loading,
    ...dataAttributes
}) => {
    const sizesAttr = typeof sizes === 'string' ? sizes : buildSizes(sizes);

    const hasRequiredNextProps = !!fill || (!!width && !!height);

    if (isNextEnv && hasRequiredNextProps) {
        return (
            <NextImage
                {...dataAttributes}
                fill={fill}
                sizes={sizesAttr}
                loader={contentfulImageResponsiveUrl}
                width={width}
                height={height}
                src={src}
                loading={loading || 'lazy'}
                style={style}
                alt={alt || ''}
                role={role}
            />
        );
    } else {
        return (
            <img
                {...dataAttributes}
                loading={loading || 'lazy'}
                decoding="async"
                src={contentfulImageUrl(src)}
                srcSet={buildContentfulImageSrcSet(src)}
                alt={alt || ''}
                role={role}
                width={width}
                height={height}
                sizes={sizesAttr}
                style={style}
            />
        );
    }
};
